'use client';

import {
  GridItem,
  LayoutGrid,
  TypographyV3 as Typography,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { type ReactNode } from 'react';

import { Image, ImageProps } from '@/app/components/ui';
import { page } from '@/app/styles/ui';
import { type Partner } from '@/configs/partner';

import NotFound from '../../ui/EmailForm/NotFound';
import EmailContent from './EmailContent';

export interface EmailLayoutProps {
  readonly title: ReactNode;
  readonly description: ReactNode;
  readonly help?: ReactNode;
  readonly imageAlt?: ImageProps['alt'];
  readonly imageSrc: ImageProps['src'];
  readonly partner: Partner;
  readonly isInvalid: boolean;
  readonly isMaintenance: boolean;
}

const EmailLayout = ({
  title,
  description,
  help,
  imageAlt = '',
  imageSrc,
  partner,
  isInvalid,
  isMaintenance,
}: EmailLayoutProps): JSX.Element => {
  const t = useTranslations();
  return (
    <LayoutGrid className={page({ pt: 'standard' })} rowGap={40}>
      <GridItem
        rowStart={{ sm: 2, md: 1 }}
        rowEnd={{ sm: 3 }}
        colStart={{ sm: 'main', md: 15, lg: 16 }}
        colEnd={{ sm: 'main', xl: 23 }}
        className="flex flex-col gap-4"
      >
        <Typography Element="h4" variant="h4">
          {title}
        </Typography>

        <Typography variant="body-large">{description}</Typography>

        {!isMaintenance &&
          (isInvalid ? <NotFound /> : <EmailContent partner={partner} />)}

        {help && <div className="mt-8">{help}</div>}
      </GridItem>

      <GridItem
        rowStart={{ sm: 1 }}
        rowEnd={{ sm: 2 }}
        colStart={{ sm: 'main', md: 'main', xl: 3 }}
        colEnd={{ sm: 8, md: 11 }}
        className="hidden justify-end sm:flex md:block"
      >
        <div className="flex aspect-square w-full max-w-96 items-center overflow-hidden rounded-lg md:max-w-none">
          <Image
            alt={imageAlt}
            role="presentation"
            src={imageSrc}
            width="600"
            height="600"
            priority
          />
        </div>
      </GridItem>

      <GridItem
        rowStart={{ sm: 1 }}
        rowEnd={{ sm: 2 }}
        colStart={{ sm: 7, md: 9, lg: 10 }}
        colEnd={{ sm: 'main', md: 13, lg: 14 }}
        className="hidden flex-col items-start justify-around sm:flex"
      >
        {[
          {
            src: t('time_asleep_app_ui'),
            width: 445,
            height: 411,
            alt: t('sleep_app_image_alt'),
            className: 'p-2',
          },
          {
            src: t('heart_rate_app_ui_2'),
            width: 537,
            height: 411,
            alt: t('heart_rate_image_2_alt'),
            className: 'p-1',
          },
        ].map(({ src, width, height, alt, className }) => (
          <div
            key={src}
            className="relative max-w-56 overflow-hidden rounded-lg md:max-w-none"
          >
            <Image
              alt=""
              role="presentation"
              src="blue-sky/home/start-day-mask-1"
              width={width}
              height={height}
            />
            <Image
              src={src}
              alt={alt}
              format="png"
              className={cx('object-contain', className)}
              fill
              sizes={`${width}px`}
            />
          </div>
        ))}
      </GridItem>
    </LayoutGrid>
  );
};

export default EmailLayout;
